/**
 * @file boardGroups.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-11-03
 * @modified 2026-11-03
 * @description The planning boards' groups, in one place so the board and the
 *              office summary cannot drift apart (Cam, 3 Nov 2026: "Please
 *              check the order of rows in the send to office email that goes
 *              from transport planning components as it seems quite confusing
 *              as it is").
 *
 *              The groups are the bulk board's (lorry day planning): one per
 *              haulier · driver · vehicle; Unassigned first; then our own
 *              lorries (Excel Agri) by their first collection time, then
 *              registration; then outside hauliers' lorries the same way; the
 *              rows someone else carries (Customer Collection, Direct
 *              Delivery, third-party haulage) last, in that order. The board
 *              keeps each group's rows in its own saved order (groupRows).
 *
 *              The office summary (officeSummaryLayout) prints the same groups,
 *              each under a heading — "Excel Agri · Jaden Pratt · H2 EXA —
 *              4 drops (6 lines) · 43.00 T · first collect 05:00" — and each
 *              group's rows in the order the lorry does them:
 *                - a day under the lorry day rules: drop order
 *                  (utils/lorryDayOrder sortByDropOrder — drop time, drop
 *                  seq, collection time, board position);
 *                - a day under the old rules (no lorry day plan), and the bag
 *                  board: collection time — a combined run takes its first
 *                  time and its rows stay together — then board position.
 *              Each row carries its collection time, its drop time (lorry
 *              day rules — only when it differs from the collection time,
 *              which the board copies into a blank drop time) and its drop
 *              number (D#) where the board knows it.
 *
 *              Which rows go in is the caller's (utils/selfBilledPo
 *              ourDeliveryRows for the summary) — nothing here adds or drops
 *              a row. Pure: no React, no fetch.
 */

import { otherCarrierOf, lorryKeyOf, minutesOf, normaliseTime, normaliseLinePlan, rowTonnes, timeFromMinutes, LORRY_DAY_UNKNOWN_VEHICLE } from './lorryDay';
import { isExcelHaulier } from './planningRows';
import { sortByDropOrder } from './lorryDayOrder';
import { formatCount, formatQuantity, formatTonnes } from './numberFormatters';

/** Who carries the rows of an "other" group, in the order the groups appear. */
export const OTHER_CARRIERS = ['Customer Collection', 'Direct Delivery', 'Third-party haulage'];

// No collection time: after every timed one.
const NO_TIME = 24 * 60;

/**
 * A row's group: { key, kind: 'unassigned' | 'lorry' | 'other', label?,
 * haulier, driver, vehicle, lorryKey } — one per haulier + driver + vehicle;
 * the rows someone else carries in a group of their carrier.
 */
export function groupOf(o) {
  const carrier = otherCarrierOf(o);
  if (carrier) return { key: `other::${carrier}`, kind: 'other', label: carrier };
  const h = String(o?.haulier || '').trim();
  const d = String(o?.driver || '').trim();
  const v = String(o?.vehicle || '').trim();
  if (!h && !d && !v) return { key: 'unassigned', kind: 'unassigned', label: 'Unassigned' };
  return { key: `lorry::${h}|${d}|${v}`, kind: 'lorry', haulier: h, driver: d, vehicle: v, lorryKey: v ? lorryKeyOf(o) : null };
}

/** The earliest collection time of some rows, in minutes (24:00 when none has one). */
export function firstCollectMinute(rows) {
  return (rows || []).reduce((m, r) => {
    const x = minutesOf(normaliseTime(r && r.scheduled_time));
    return x == null ? m : Math.min(m, x);
  }, NO_TIME);
}

const groupRank = (g) => (g.kind === 'unassigned' ? 0 : g.kind === 'lorry' ? 1 : 2);

/**
 * The board's group order: Unassigned first; our lorries (Excel Agri) before
 * outside hauliers', each by first collection then registration; the groups
 * someone else carries last. Each group needs its `firstTime` (groupRows).
 */
export function compareGroups(a, b) {
  return (groupRank(a) - groupRank(b))
    || (a.kind === 'other' ? OTHER_CARRIERS.indexOf(a.label) - OTHER_CARRIERS.indexOf(b.label) : 0)
    || ((isExcelHaulier(b.haulier) ? 1 : 0) - (isExcelHaulier(a.haulier) ? 1 : 0))
    || (a.firstTime - b.firstTime)
    || `${a.vehicle || ''} ${a.driver || ''} ${a.haulier || ''}`.localeCompare(`${b.vehicle || ''} ${b.driver || ''} ${b.haulier || ''}`);
}

/**
 * The rows in the board's groups, in the board's group order. Each group's
 * rows stay in the order they came in (the board's own saved order); each
 * group carries `rows` and `firstTime`.
 */
export function groupRows(rows) {
  const map = new Map();
  for (const o of rows || []) {
    const g = groupOf(o);
    if (!map.has(g.key)) map.set(g.key, { ...g, rows: [] });
    map.get(g.key).rows.push(o);
  }
  const groups = [...map.values()];
  for (const g of groups) g.firstTime = firstCollectMinute(g.rows);
  return groups.sort(compareGroups);
}

/**
 * A lorry's rows by collection time — the old rules (no lorry day plan) and
 * the bag board. A combined run takes the first time of any of its rows and
 * its rows stay together; rows with no time go last; ties keep the order
 * they came in (the board's).
 */
export function collectionOrder(rows) {
  const list = rows || [];
  const runFirst = new Map();
  const runAt = new Map();
  list.forEach((r, i) => {
    const run = r && r.run_key ? String(r.run_key) : null;
    if (!run) return;
    if (!runAt.has(run)) runAt.set(run, i);
    const m = minutesOf(normaliseTime(r.scheduled_time));
    if (m != null) runFirst.set(run, Math.min(runFirst.has(run) ? runFirst.get(run) : NO_TIME, m));
  });
  const keyOf = (r, i) => {
    const run = r && r.run_key ? String(r.run_key) : null;
    if (run) return [runFirst.has(run) ? runFirst.get(run) : NO_TIME + 1, runAt.get(run), i];
    const m = minutesOf(normaliseTime(r && r.scheduled_time));
    return [m == null ? NO_TIME + 1 : m, i, i];
  };
  return list
    .map((r, i) => ({ r, k: keyOf(r, i) }))
    .sort((a, b) => (a.k[0] - b.k[0]) || (a.k[1] - b.k[1]) || (a.k[2] - b.k[2]))
    .map(x => x.r);
}

/** Drops a group makes: its distinct drop numbers under the lorry day rules (a row with none counts alone), else one a row. */
function dropCountOf(rows, { newRules, dropNumberOf }) {
  if (!newRules) return rows.length;
  const numbers = new Set();
  let loose = 0;
  for (const r of rows) {
    const n = dropNumberOf(r);
    if (n == null) loose += 1; else numbers.add(n);
  }
  return numbers.size + loose;
}

/**
 * The summary's groups: the board's groups (groupRows), each group's rows in
 * the order the lorry does them, with its tonnes, drops and first collection.
 * @param {object[]} rows
 * @param {object}   [opts]
 * @param {boolean}  [opts.newRules]      the day is under the lorry day rules
 * @param {Function} [opts.dropNumberOf]  row → its drop number, or null
 */
export function summaryGroups(rows, { newRules = false, dropNumberOf = () => null } = {}) {
  return groupRows(rows).map(g => {
    const ordered = newRules ? sortByDropOrder(g.rows) : collectionOrder(g.rows);
    return {
      ...g,
      rows: ordered,
      tonnes: ordered.reduce((s, r) => s + rowTonnes(r), 0),
      dropCount: g.kind === 'lorry' ? dropCountOf(ordered, { newRules, dropNumberOf }) : ordered.length,
      firstCollect: g.firstTime < NO_TIME ? timeFromMinutes(g.firstTime) : null
    };
  });
}

/** The words for a vehicle in a heading: "XYN 670", "Unknown vehicle", "No vehicle". */
const vehicleWords = (v) => (!v ? 'No vehicle' : v === LORRY_DAY_UNKNOWN_VEHICLE ? 'Unknown vehicle' : v);

/**
 * A group's heading: { title, facts } — "Excel Agri · Jaden Pratt · H2 EXA" /
 * "4 drops (6 lines) · 43.00 T · first collect 05:00"; "Not yet assigned (3)" /
 * "22.00 T · first collect 06:00".
 */
export function groupHeading(g) {
  const n = (g.rows || []).length;
  const lines = `${formatCount(n)} line${n === 1 ? '' : 's'}`;
  const tonnes = formatTonnes(g.tonnes || 0);
  const first = g.firstCollect ? `first collect ${g.firstCollect}` : 'no collection time yet';
  if (g.kind === 'unassigned') return { title: `Not yet assigned (${formatCount(n)})`, facts: [tonnes, first].join(' · ') };
  if (g.kind === 'other') return { title: `${g.label} (${formatCount(n)})`, facts: tonnes };
  const title = [g.haulier || 'No haulier', g.driver || 'No driver', vehicleWords(g.vehicle)].join(' · ');
  const d = g.dropCount == null ? n : g.dropCount;
  const drops = `${formatCount(d)} drop${d === 1 ? '' : 's'}${d !== n ? ` (${lines})` : ''}`;
  return { title, facts: [drops, tonnes, first].join(' · ') };
}

/**
 * The office summary's rows, grouped as the board groups them.
 * @param {object[]} rows                 our deliveries on the day (ourDeliveryRows)
 * @param {object}   opts
 * @param {Function} opts.toDelivery      row → the summary's row (each board's own)
 * @param {boolean}  [opts.newRules]      the day is under the lorry day rules
 * @param {Function} [opts.dropNumberOf]  row → its drop number (D#), or null
 * @returns {{ groups: object[], deliveries: object[] }} `deliveries` is every
 *          group's rows in turn — the flat list, in the same order.
 */
export function officeSummaryLayout(rows, { toDelivery, newRules = false, dropNumberOf = () => null } = {}) {
  const dropOf = (r) => {
    const n = Number(dropNumberOf(r));
    return Number.isInteger(n) && n > 0 ? n : null;
  };
  let seq = 0;
  const groups = summaryGroups(rows, { newRules, dropNumberOf: dropOf }).map(g => {
    const { title, facts } = groupHeading(g);
    return {
      key: g.key,
      kind: g.kind,
      title,
      facts,
      haulier: g.haulier || '',
      driver: g.driver || '',
      vehicle: g.vehicle || '',
      count: g.rows.length,
      dropCount: g.dropCount,
      tonnes: formatQuantity(g.tonnes || 0),
      firstCollect: g.firstCollect || '',
      rows: g.rows.map(r => {
        seq += 1;
        const n = dropOf(r);
        const collect = normaliseTime(r.scheduled_time) || '';
        // The drop time only when it says something: the board fills a blank
        // drop time from the collection time, so the two are often the same.
        const drop = newRules ? (normaliseLinePlan(r.line_plan).dropTime || '') : '';
        return {
          ...(toDelivery ? toDelivery(r) : {}),
          seq,
          collectTime: collect,
          dropTime: drop && drop !== collect ? drop : '',
          dropLabel: n ? `D${n}` : ''
        };
      })
    };
  });
  return { groups, deliveries: groups.flatMap(g => g.rows) };
}
